import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { StateBadge } from '@/components/console/StateBadge';
import { MsgBubble } from './MsgBubble';
import { AnswerBar, answerBarMode, answerBarWritable, type AnswerBarMode } from './AnswerBar';
import type { ProposedReplyActions } from './ProposedReplies';
import { customerAskedForPerson } from '@/utils/consultationReason';
import { CloseConversationDialog } from './CloseConversationDialog';
import { TimelineFilter } from './TimelineFilter';
import { DraftReviewCard, type DraftAction } from './DraftReviewCard';
import { SpamConfirmDialog } from './SpamConfirmDialog';
import { TagPicker } from './TagPicker';
import { ReturnToTeamDialog } from './ReturnToTeamDialog';
import { ConversationMenu } from './ConversationMenu';
import { ContinuesLink } from './ContinuesLink';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { useAuthStore } from '@/stores/authStore';
import { useAgentStore } from '@/stores/agentStore';
import { useConsole } from '@/console/ConsoleContext';
import { useOwnerVerification } from '@/console/useVerifiedOwner';
import { canReturnToTeam } from '@/console/folders';
import { lockedByOtherOwner, ownerDisplay } from '@/utils/ownership';
import {
  classifyChannel,
  getConversationLabel,
  getCustomerDisplayName,
  isAnonymousCustomer,
} from '@/utils/conversationLabel';
import { useCustomerCompany } from '@/hooks/useCustomerCompany';
import { usePermission } from '@/hooks/usePermission';
import { channelIconName, channelColor } from '@/components/ui/Icon';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { markSessionSpam, serverErrorText } from '@/api/spam';
import { isConsultationChannel, storedMessageToUiMessage } from '@/utils/storedMessage';
import {
  buildMergedTimeline,
  filterTimeline,
  timelineCounts,
  timelineEntryFromDto,
  type TimelineFilter as TimelineFilterValue,
} from '@/utils/timeline';
import type {
  Conversation,
  LearningTag,
  Message,
  MessageAttachment,
  TimelineEntry,
  TimelinePageResponse,
  TranscriptPageResponse,
} from '@/types';

/**
 * ONE conversation, ONE timeline.
 *
 * The customer-facing chat, the internal bot<->staff consultation and the staff-only action timeline ("Dana
 * took over") are merged into a single scroller by time, with an avatar on every message. A segmented filter
 * (All / Customer chat / Consultation, with counts) narrows what is SHOWN; it never changes where an answer
 * goes - that is decided by the conversation's state and owner alone.
 *
 * Replaces ThreadView's two tabs (and the layout toggle that split them side by side).
 */
export const ChatView: React.FC<{ conv: Conversation }> = ({ conv }) => {
  const { t, channelLabel } = useT();
  const navigate = useNavigate();
  const { hub, refreshRow, refreshCounts } = useConsole();

  const messages = useConversationStore((s) => s.messages);
  const setMessages = useConversationStore((s) => s.setMessages);
  const timelineEntries = useConversationStore((s) => s.timeline[conv.sessionId]);
  const hydrateTimeline = useConversationStore((s) => s.hydrateTimeline);
  const consultLog = useConversationStore((s) => s.consultationLog[conv.sessionId]);
  const appendConsultationMessage = useConversationStore((s) => s.appendConsultationMessage);
  const markClosed = useConversationStore((s) => s.markClosed);
  const markSpamHidden = useConversationStore((s) => s.markSpamHidden);
  const isBotThinking = useConversationStore((s) => s.botThinking[conv.sessionId] ?? false);
  const user = useAuthStore((s) => s.user);
  const { hasPermission, roles, isTenantAdmin } = usePermission();
  // May this viewer ACT on a conversation at all (not merely read it)? The one permission every action below - and the
  // answer bar - starts from, exactly as console/folders.ts `isReadOnlyFor` / `canTakeOver` do. UX only: the server
  // re-checks it on every hub method and request.
  const canHandle = hasPermission('workspace.handle');

  const sessionId = conv.sessionId;
  // The timeline filter is LOCAL to the open conversation: it starts at All, resets to All whenever another
  // conversation is opened, and is never remembered anywhere (no localStorage) - a filter left on "Consultation" must
  // not make the next customer's chat look empty. It only narrows what is SHOWN; the answer bar below is decided by
  // state and owner, never by this. The choice is stored WITH its session and is RESET (state adjusted during render:
  // React throws the render away and runs it again with the reset value, so there is no frame of the previous
  // conversation's filter and no effect) the moment another session is shown - so coming back to an earlier
  // conversation in the same mounted view opens on All as well, not on what was chosen there before.
  const [filterChoice, setFilterChoice] = useState<{ sessionId: string; value: TimelineFilterValue }>({
    sessionId,
    value: 'all',
  });
  if (filterChoice.sessionId !== sessionId) setFilterChoice({ sessionId, value: 'all' });
  const filter: TimelineFilterValue = filterChoice.value;
  const changeFilter = (f: TimelineFilterValue) => setFilterChoice({ sessionId, value: f });
  const [actionError, setActionError] = useState(false);
  // Close goes through a confirm that also carries the "teach the bot from this chat" choice (CloseConversationDialog).
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // OP-1005 - "Spam / unwanted" is offered ONLY on an email conversation, and only to a user holding
  // workspace.handle (the permission the server enforces on the three spam endpoints). Both are UX:
  // the server independently refuses a non-email session (400) and a caller without the permission.
  // The channel test is the console's own classifier (case-insensitive), not a bare string compare.
  const canMarkSpam = canHandle && classifyChannel(conv.channel) === 'email';
  const [spamOpen, setSpamOpen] = useState(false);
  const [spamBusy, setSpamBusy] = useState(false);
  const [spamError, setSpamError] = useState<string | null>(null);

  const isConsulting = conv.state === 'CONSULTATION_REQUESTED';
  const isTakenOver = conv.state === 'HUMAN_TAKEOVER';
  // A consultation the ADR-016 router targeted at a teammate who has not answered is still TEAM SHARED (operator decision
  // D3): it is a label ("Routed to {name}"), never a lock. Nobody is kept from taking it or from letting the bot continue.
  const routedToTeammate = isConsulting && conv.assignedToOther === true;
  const directory = useAgentStore((s) => s.directory);
  const routedName = routedToTeammate && conv.assignedAgentId ? directory[conv.assignedAgentId] : undefined;
  const closed = conv.state === 'CLOSED';

  // WHO owns this conversation, for what the header offers. One shared derivation (utils/ownership.ts: the wire
  // ownerAgentId, no fallback), VERIFIED against the server by useOwnerVerification (console/useVerifiedOwner.ts) so
  // the contact panel reads the very same owner. A row's named owner is only as fresh as the row: the state push that flips it into HUMAN_TAKEOVER moves `state` only,
  // and a tab can hold a row for hours. So the owner is treated as UNKNOWN - read-only banner, no composer, no Take
  // over, fail closed - on a transition into HUMAN_TAKEOVER AND when this view mounts on a row that already reads
  // HUMAN_TAKEOVER, until GET /api/sessions/{id} succeeds with this session's row for the current episode. An owner
  // that cannot be matched to the signed-in agent counts as SOMEONE ELSE: unknown fails closed.
  const me = user?.agentId ?? null;
  const { owner, holder, ownerUnverified, ownerConfirming } = useOwnerVerification(conv);
  const mineOwned = !!owner && !!me && owner === me;
  // "Someone else holds it" locks this console while the server says private ownership is in effect
  // (`ownershipEnforced`, wire value; false for every tenant until PR-A2), and - before that - ONLY on a live human
  // takeover by a teammate. A recorded owner of a bot-handled or consulted conversation is a record, not a lock
  // (utils/ownership.ts lockedByOtherOwner). WHO holds it is the HOLDER (utils/ownership.ts holderId): the owner under
  // private ownership, but the live CONTROLLER (`assignedAgentId`) in legacy shared mode - PR-A1 only records a claim
  // on an unowned row, so when B takes over what A guided the owner stays A while B holds the customer, and B must be
  // able to write while A must not.
  const ownedByOther = lockedByOtherOwner(conv, holder, me);
  // A human holds the conversation and nobody can be named as the holder (or the row is not yet verified): not mine to
  // act on - unknown fails closed, for the header, the bar, the tag editor and the panel alike. The wire owner being
  // null is NOT the test: a takeover that predates the ownership backfill has no owner and a perfectly good controller.
  const holderUnknown = isTakenOver && !holder;
  const mineTakeover = isTakenOver && !!holder && !!me && holder === me;

  // PR-A: the header names the conversation's OWNER (the verified wire owner - the same one the contact panel's "Owner"
  // row shows, through the same naming rule). 'Team shared' only when the SERVER said nobody owns it (`null`); while the
  // owner is unverified or the row never said (undefined), nothing is named - an unknown owner is not "team shared".
  const headerOwner = ownerUnverified
    ? null
    : owner
      ? ownerDisplay(owner, me, directory, t)
      : conv.ownerAgentId === null
        ? ownerDisplay(null, me, directory, t)
        : null;

  // "Return to team" (ADR-033 D2): the OWNER may always give a conversation up; on one somebody else owns it is offered
  // ONLY to a tenant admin (never a senior agent, never a plain agent, never a viewAll-only reader). Judged on the
  // VERIFIED owner (`owner` is null while the server has not confirmed it - useOwnerVerification - and canReturnToTeam
  // offers nothing for a null owner, so an owner this console cannot yet prove is never offered "return"), never on a
  // closed conversation (no Reopen, so nothing to hand back), and only to someone holding workspace.handle -
  // the permission the hub method demands. UX only: the server refuses a non-owner non-admin itself.
  const returnMode: 'mine' | 'admin' | null =
    !closed &&
    canHandle &&
    canReturnToTeam({ ...conv, ownerAgentId: owner }, me, roles)
      ? mineOwned
        ? 'mine'
        : 'admin'
      : null;
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnBusy, setReturnBusy] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);
  // The offer is gone (the owner changed, the row closed, the roles changed): so is any half-answered confirm.
  useEffect(() => {
    if (returnMode === null) {
      setReturnOpen(false);
      setReturnError(null);
    }
  }, [returnMode]);

  // Tags are editable by someone who may ACT on this conversation: holds workspace.handle, and it is not a teammate's
  // (or an owner this console cannot yet name during a human takeover: `owner` is null while it is unverified, so that
  // case is covered by the last clause too). Closed stays editable - tags change after
  // close while the ticket exists (D6/D11). UX only: the server re-checks handle + ownership on every attach/detach.
  // `ownerUnverified` also covers a row the server has never described (a push or draft placeholder, or an enforced
  // answer with no owner stated): not known to be mine, so not editable until it has been re-read.
  //
  // Operator decision 2026-10-19: a TENANT ADMIN may also tag a conversation a teammate privately owns (a label is the
  // tenant's own bookkeeping, not an act toward the customer). Everything else stays locked for them - the server grants
  // this only to the `admin` of the conversation's own tenant and answers 404 to anyone else.
  const tagsReadOnly = !canHandle || (!isTenantAdmin && (ownedByOther || ownerUnverified || holderUnknown));
  const convTags = Array.isArray(conv.tags) ? conv.tags : [];

  // Take over, Close, Let bot continue and Spam act on the conversation, so they wait for a viewer who may ACT (holds
  // workspace.handle - the same first test as folders.ts `isReadOnlyFor` / `canTakeOver`, and the permission the hub
  // methods demand; a viewer who may only read gets none of them) and for a KNOWN owner that is me (or nobody). When that
  // stops being true - the owner turns out to be a teammate, or becomes unverified after a transition, or the roles
  // change under a token refresh - anything half-done is dropped: an open Spam dialog and an open Close dialog are both
  // closed, so neither can be confirmed on a conversation that is no longer mine to act on. (Declared before use; the
  // effect reads it, the render guards use it.)
  const actionsBlocked = !canHandle || ownedByOther || ownerUnverified || holderUnknown;
  useEffect(() => {
    if (!actionsBlocked) return;
    setSpamOpen(false);
    setSpamError(null);
    setCloseOpen(false);
    setCloseError(null);
  }, [actionsBlocked]);

  // Load the transcript when the active session changes. Customer rows go to the message list, consultation rows
  // to the consultation log; the merged timeline puts them back together by time.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get<TranscriptPageResponse>(API.CHAT.MESSAGES(sessionId), {
          params: { pageSize: 100 },
        });
        if (cancelled) return;
        const stored = res.data?.messages ?? [];
        setMessages(stored.filter((m) => !isConsultationChannel(m.channel)).map(storedMessageToUiMessage));
        // OP-1516: the persisted rows are the system of record (one row per ask, its own text and time) and replace any
        // card already held for the same ask (the queue rehydrate, a push).
        stored
          .filter((m) => isConsultationChannel(m.channel))
          .map(storedMessageToUiMessage)
          .forEach((m) => appendConsultationMessage(sessionId, m, { authoritative: true }));
      } catch {
        if (!cancelled) setMessages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setMessages, appendConsultationMessage]);

  // Chat action timeline (staff-only): who took over, who the bot consulted, who closed it. Loaded beside the
  // transcript and merged into the customer chat by time. Best effort and independent of the transcript - a
  // failed or empty load simply shows no lines, and never blocks or empties the messages. The load MERGES into
  // the store (it does not replace), so a live ReceiveTimelineEntry that landed while this was in flight stays.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get<TimelinePageResponse>(API.CHAT.TIMELINE(sessionId), {
          params: { pageSize: 100 },
        });
        if (cancelled) return;
        const raw = res?.data?.entries;
        if (!Array.isArray(raw)) return;
        const entries = raw
          .map((e) => timelineEntryFromDto(e, sessionId))
          .filter((e): e is TimelineEntry => e !== null);
        hydrateTimeline(sessionId, entries);
      } catch {
        /* the thread is complete without its timeline lines */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrateTimeline]);

  // ONE list: customer messages + the consultation log + the action-timeline lines, interleaved by time. The
  // messages keep their arrival order; the other two are placed among them by timestamp (buildMergedTimeline).
  const merged = useMemo<Message[]>(
    () => buildMergedTimeline(messages, consultLog, timelineEntries),
    [messages, consultLog, timelineEntries],
  );
  const counts = useMemo(() => timelineCounts(merged), [merged]);
  const shown = useMemo(() => filterTimeline(merged, filter), [merged, filter]);

  // Follow the newest row: on a new conversation, a new row, a filter change and when the bot starts thinking.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [sessionId, shown.length, filter, isBotThinking]);

  // The PERSON, not the thread — this one local feeds the avatar's initials, the header line and
  // its contact-page LINK, and both composer placeholders ("Message {first}…").
  // getConversationLabel names the THREAD, which for email is the SUBJECT LINE, so every one of
  // those used to caption the customer with their own subject — and an email that arrived without
  // one captioned them "(no subject)" and offered that as the link to their contact record.
  // getCustomerDisplayName is the sibling helper written for exactly this distinction; it returns
  // '' when nobody's name is known, so the thread label stays as the fallback rather than leaving
  // the header blank.
  const customerName = getCustomerDisplayName(conv) || getConversationLabel(conv);
  // CRM v2 WS-6.2 — "who is this person's company", the second question an agent asks after "who is
  // this". Fail-soft and silent; a projection over the shared useCustomerProfile fetch.
  const company = useCustomerCompany(conv.customerId);

  const pendingDraft = conv.pendingDraft;
  // For a conversation another agent owns the card is shown, never actionable: `assignedToOther` with an
  // assignee that is not me is exactly what DraftReviewCard reads as read-only.
  const cardDraft =
    pendingDraft && (ownedByOther || ownerUnverified)
      ? { ...pendingDraft, assignedToOther: true, assignedAgentId: owner }
      : pendingDraft;
  const onDraftAct = (action: DraftAction, text?: string) => {
    if (!pendingDraft) return;
    return runAction(() =>
      hub.actOnEmailDraft(sessionId, pendingDraft.draftId, action, text, pendingDraft.revision),
    );
  };

  // ADR-019 D7 — the viewing beacon that defers auto-send.
  //
  // 2026-08-02, operator rule: "opened by an agent → held until he gets out of the view of the
  // draft/chat, then delay it by another 15 minutes."
  //
  // This used to fire EXACTLY ONCE per draftId. That satisfies "opening resets the Idle clock"
  // and nothing more: an agent who opened a draft and kept reading it saw the touch go stale
  // after IdleMinutes (15) and the sweeper auto-sent the draft out from under them. To hold a
  // draft while it is actually on screen, the touch has to REPEAT while the view is open.
  //
  // So: a heartbeat on an interval for as long as this draft is mounted and the tab is visible,
  // plus a final touch on unmount so the 15-minute idle window starts when the agent LEAVES
  // rather than at the last tick. Cleanup runs on draft change and on unmount, so switching
  // conversations stops the old draft's heartbeat.
  //
  // Interval mirrors the server's Email.DraftReview.ViewDebounceSeconds (default 30): the
  // consumer debounces writes at that period, so ticking faster is pure noise.
  const VIEW_HEARTBEAT_MS = 30_000;

  const draft = conv.pendingDraft;
  const draftId = draft?.draftId ?? null;
  // A read-only awareness copy is not a review: a supervisor merely being aware must not defer
  // the auto-send. Only beat while this console can actually act.
  // Nor is a conversation another agent owns: the card is forced read-only for it (below) and the view
  // heartbeat stops, so looking at somebody else's conversation never defers the auto-send either.
  // The same fail-closed set as `actionsBlocked`, and it must stay the same set: a takeover row nobody is named on
  // (`holderUnknown`) is not this console's to act on, so merely having it open must not defer the auto-send either
  // (the round-2 guard was folded into actionsBlocked and tagsReadOnly but not here).
  const draftIsReadOnly =
    !draft ||
    !canHandle ||
    ownedByOther ||
    ownerUnverified ||
    holderUnknown ||
    (draft.assignedToOther === true && draft.assignedAgentId !== me);
  // The interval reads the CURRENT revision without re-arming: keying the effect on the whole
  // draft object would tear down and rebuild the heartbeat on every unrelated store update, and
  // would re-fire an immediate touch on a mere revision bump.
  const draftRevisionRef = useRef(draft?.revision);
  draftRevisionRef.current = draft?.revision;

  useEffect(() => {
    if (!draftId || draftIsReadOnly) return;

    // Never wrapped in runAction — a failed idle-clock touch must not flash the red error
    // banner at the agent. The trailing catch is defence-in-depth so this effect can never
    // produce an unhandled rejection regardless of what the hub does.
    const touch = () => {
      if (typeof document !== 'undefined' && document.hidden) return; // a hidden tab is not "viewing"
      void hub
        .actOnEmailDraft(sessionId, draftId, 'view', undefined, draftRevisionRef.current)
        .catch(() => {});
    };

    touch(); // opening the draft
    const timer = setInterval(touch, VIEW_HEARTBEAT_MS);
    // No touch on cleanup: the last beat is at most VIEW_HEARTBEAT_MS old, which is negligible
    // against IdleMinutes (15). Leaving simply stops the beats, so the idle window starts within
    // ~30 s of the agent looking away — and the count stays "one touch per open", which is what
    // the suite pins.
    return () => clearInterval(timer);
  }, [draftId, draftIsReadOnly, sessionId, hub]);

  const runAction = async (fn: () => Promise<void>) => {
    setActionError(false);
    try {
      await fn();
    } catch {
      setActionError(true);
      window.setTimeout(() => setActionError(false), 4000);
    }
  };

  // A state-changing action the SERVER may refuse (Take over, Close, Release, Let bot continue). When it fails - and a
  // refusal ("Session not available": the conversation became somebody else's, or is not visible to us any more) is exactly
  // the case that matters - THIS row is re-read before the error is surfaced, so the console learns from the server why
  // (a 404 hides the row through the reversible hidden set, a new owner locks the bar) instead of leaving live actions on
  // a row it may no longer be allowed to touch. Any failure re-reads: a re-read of a row that is fine is harmless, and
  // "refused" cannot be told from "could not reach the server" reliably enough to skip it.
  const runStateChange = (fn: () => Promise<void>) =>
    runAction(async () => {
      try {
        await fn();
      } catch (err) {
        void refreshRow(sessionId);
        throw err;
      }
    });

  // Takeover: the hub invoke is unchanged. Afterwards THIS row is re-read (refreshRow) so the header and the answer bar
  // learn WHO now owns the conversation from the server (the live state push carries the new state only); until that
  // answer lands the owner is not trusted ("Confirming who owns this conversation"). The client never sets the owner
  // itself. A refused takeover re-reads the row too (runStateChange).
  const doTakeover = () =>
    runStateChange(async () => {
      await hub.issueTakeover(sessionId, 'agent_takeover');
      void refreshRow(sessionId);
    });

  // 2026-10-28 (operator) - "The customer asked to talk to a person": shown while the bot is waiting for the team BECAUSE
  // the customer asked for a human (utils/consultationReason.ts: CONSULTATION_REQUESTED, so nobody has taken over, it is
  // not closed and not dismissed - each of those moves the state - and the newest consultation reason is CustomerKeyword).
  // The Take over in it is the header's own action (doTakeover -> hub.issueTakeover), offered under the header's own
  // condition; a viewer who may not act sees the line without the button.
  const askedForPerson = customerAskedForPerson(conv);
  const canTakeOverHere = !closed && !mineTakeover && !actionsBlocked;

  // 2026-10-28 - the proposed replies under a bot consultation note send to the CUSTOMER, so they are offered only to a
  // viewer the answer bar would give a composer to (AnswerBar.answerBarWritable - the same gate, not a copy), and not
  // while the owner is unverified (actionsBlocked, as for every other act in this view). Otherwise no reply is shown.
  const canActOnReplies = !actionsBlocked && answerBarWritable(conv, holder, me, canHandle);
  // Edit puts the reply into the composer of the CURRENT mode only; a mode flip or another conversation drops it.
  const barMode: AnswerBarMode = answerBarMode(conv.state, holder, me);
  const [prefill, setPrefill] = useState<{ sessionId: string; text: string; seq: number; mode: AnswerBarMode } | null>(
    null,
  );
  const prefillSeqRef = useRef(0);
  const replyActions: ProposedReplyActions | null = canActOnReplies
    ? {
        send: async (noteMessageId, index) => {
          try {
            await hub.sendProposedReply(sessionId, noteMessageId, index);
          } catch (err) {
            // Refused or failed: nothing was sent. Re-read this row so the console learns from the server whether it is
            // still ours (the same as every other refused act here), then let the chips say "not sent".
            void refreshRow(sessionId);
            throw err;
          }
        },
        edit: (text) => {
          prefillSeqRef.current += 1;
          setPrefill({ sessionId, text, seq: prefillSeqRef.current, mode: barMode });
        },
      }
    : null;
  const barPrefill =
    prefill && prefill.sessionId === sessionId && prefill.mode === barMode
      ? { text: prefill.text, seq: prefill.seq, mode: prefill.mode }
      : null;
  useEffect(() => {
    if (prefill && (prefill.mode !== barMode || prefill.sessionId !== sessionId)) setPrefill(null);
  }, [prefill, barMode, sessionId]);

  const sendCustomer = (text: string, attachmentIds?: string[]) =>
    hub.sendCustomerMessage(sessionId, text, attachmentIds);
  const sendGuidance = async (text: string, attachmentIds?: string[], attachments?: MessageAttachment[]) => {
    const optimistic: Message = {
      // 2026-07-23 — a random id, not derived from `messages.length` (the
      // CUSTOMER-chat array, not the consultation log — a mismatched-array
      // bug that could collide across sessions). conversationStore's
      // appendConsultationMessage reconciles this optimistic entry against
      // the later-persisted real row by content + timestamp, not by id.
      messageId: `local-guidance-${crypto.randomUUID()}`,
      sessionId,
      sender: 'AGENT',
      // The signed-in user's own id, so the bubble labels it "You" by the same rule as every other agent row.
      senderId: user?.agentId ?? 'me',
      content: text,
      contentType: 'text',
      timestamp: new Date().toISOString(),
      stream: 'consult',
      // 2026-10-20: the picture shows in the agent's own echo at once; the stored row (same files, server-resolved)
      // replaces this entry when it arrives. Absent, not empty, when there are none.
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
    };
    appendConsultationMessage(sessionId, optimistic);
    if (attachmentIds && attachmentIds.length > 0) {
      await hub.sendGuidance(sessionId, text, conv.channel || 'web', attachmentIds);
    } else {
      await hub.sendGuidance(sessionId, text, conv.channel || 'web');
    }
  };

  const openCloseDialog = () => {
    setCloseError(null);
    setCloseOpen(true);
  };

  /**
   * Closes the conversation from the confirm dialog. A CHANGED learning choice is saved first, because learning reads it
   * when the conversation closes; if that save fails nothing is closed and the dialog says so (the agent retries or
   * cancels). An unchanged choice sends nothing extra.
   */
  const confirmCloseWith = async (chosen: LearningTag, saved: LearningTag) => {
    if (closeBusy) return;
    if (chosen !== saved) {
      setCloseBusy(true);
      setCloseError(null);
      try {
        await axiosClient.post(API.LEARNING.SET_TAG(sessionId), { tag: chosen });
      } catch {
        setCloseBusy(false);
        setCloseError(t('learningFailedToSave'));
        return;
      }
      setCloseBusy(false);
    }
    setCloseOpen(false);
    await runStateChange(async () => {
      await axiosClient.post(API.SESSIONS_CLOSE(sessionId), {
        closedBy: user?.agentId ?? 'agent',
        reason: 'agent_closed',
      });
      markClosed(sessionId);
      void refreshRow(sessionId);
    });
  };

  const doReturnToTeam = async () => {
    if (returnBusy) return;
    setReturnBusy(true);
    setReturnError(null);
    try {
      await hub.returnToTeam(sessionId);
    } catch {
      // A refusal ("Session not available") or any failure means NOT returned. Say so, and re-read the row: the
      // console learns who owns it now from the server, it does not guess.
      setReturnBusy(false);
      setReturnError(t('return_team_failed'));
      void refreshRow(sessionId);
      return;
    }
    setReturnBusy(false);
    setReturnOpen(false);
    // The row leaves My work when the server's ownership push or this re-read lands - never optimistically here. A
    // successful return can still leave the state HUMAN_CONTROLLED (another agent holds live control), so the answer of
    // the invoke is not what the row is shown from.
    void refreshRow(sessionId);
    refreshCounts();
  };

  const openSpamDialog = () => {
    setSpamError(null);
    setSpamOpen(true);
  };

  const cancelSpam = () => {
    if (spamBusy) return;
    setSpamOpen(false);
    setSpamError(null);
  };

  /**
   * Marks this conversation as spam / unwanted.
   *
   * The row is removed ONLY after the server has said yes. A failed POST leaves the conversation
   * exactly where it was and shows the server's own error text inside the dialog - never a silent
   * reset, never an optimistic hide that a refusal would then have to undo.
   */
  const confirmSpam = async (blockSender: boolean) => {
    setSpamBusy(true);
    setSpamError(null);
    try {
      await markSessionSpam(sessionId, blockSender);
    } catch (err) {
      setSpamError(serverErrorText(err) ?? t('spam_err_generic'));
      setSpamBusy(false);
      return;
    }
    setSpamBusy(false);
    setSpamOpen(false);
    // Tombstones the session AND drops its row, transcript and consultation log from local state;
    // ReceiveSessionHidden (which the server also sends to this console) lands on the same call.
    markSpamHidden(sessionId);
    // The open thread is gone: leave its URL too, so a refresh does not try to reopen it.
    navigate('/conversations', { replace: true });
  };

  return (
    <div className="ac-thread-wrap ac-chat" data-testid="thread-view">
      <div className="ac-thread-head">
        <Avatar name={customerName} seed={sessionId} size={34} />
        <div className="ac-thread-head-info">
          {/*
            The customer's name doubles as the link to their contact page.

            Not decoration: the responsive guard hides the contact panel (.cp) below 1180px, and the panel's
            "View contact" button with it — so without this the contact page would have NO entrance
            at all on a narrow screen. This is also where an agent already looks for "who is this".
            Anonymous visitors have no contact record, so they stay plain text rather than offering a
            link that can only apologise.
          */}
          {isAnonymousCustomer(conv.customerId) ? (
            <div className="ac-thread-name">{customerName}</div>
          ) : (
            <button
              type="button"
              className="ac-thread-name ac-thread-name-link"
              onClick={() => navigate(`/contacts/${encodeURIComponent(conv.customerId)}`)}
              title={t('ctx_view_contact')}
              data-testid="thread-contact-link"
            >
              {customerName}
            </button>
          )}
          {/*
            The company, subordinate to the customer name (CRM v2 WS-6.2).

            On its OWN line rather than appended to the name line or the meta line below. The meta
            line carries the state badge, and a long company name sharing that row would wrap the
            badge onto a line of its own in a narrow pane — the state of the conversation is the one
            thing in this header an agent must never have to hunt for. Its own line cannot move it.
            Truncation (not wrapping) keeps the header exactly two lines tall whatever the CRM holds;
            the full value stays available as a title tooltip.
          */}
          {company.name && (
            <div
              data-testid="thread-company"
              title={company.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                minWidth: 0,
                marginTop: 1,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-muted)',
              }}
            >
              <Icon name="building" size={12} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {company.name}
              </span>
            </div>
          )}
          <div className="ac-thread-meta">
            <Icon
              name={channelIconName(conv.channel)}
              size={13}
              style={{ color: channelColor(conv.channel) }}
            />
            {channelLabel(conv.channel)} · <StateBadge state={isTakenOver ? 'HUMAN_TAKEOVER' : conv.state} />
            {headerOwner && (
              <span
                className={`ac-thread-owner${headerOwner.mine ? ' mine' : ''}${owner ? '' : ' team'}`}
                data-testid="thread-owner"
                data-owner={owner ? (headerOwner.mine ? 'me' : 'other') : 'team'}
                title={headerOwner.title ? `${t('conv_owner')}: ${headerOwner.title}` : t('conv_owner')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {' · '}
                <Icon name={owner ? 'user' : 'users'} size={12} />
                <span dir="auto">{headerOwner.text}</span>
              </span>
            )}
            {routedToTeammate && (
              <span
                className="ac-badge low"
                data-testid="routed-to-indicator"
                title={t('routedToBadgeTitle')}
              >
                <span dir="auto">
                  {routedName ? t('routedToBadge', { name: routedName }) : t('routedToBadgeUnknown')}
                </span>
              </span>
            )}
            {/* D7: a returning customer's new conversation links back to the closed one the SERVER linked it to. It sits
                INSIDE the meta line, never on a line of its own: a fourth header line pushed the header taller than the list
                header beside it (operator report 2026-10-26). Reads only the server-stamped previousSessionId; renders
                nothing without it. The contact panel carries the same link. */}
            <ContinuesLink previousSessionId={conv.previousSessionId} testId="thread-continues-link" />
          </div>
        </div>
        <div className="ac-thread-actions">
          {/* Every action below is offered by who OWNS the conversation (see `owner` above), not just by its
              state: the owner's conversation is read-only to everyone else, so a teammate is offered nothing
              that would act on it. And while the owner is UNVERIFIED (a fallback owner not yet confirmed by the
              server) nothing that acts on the conversation is offered either - Take over, Close and Spam all wait.
              UX only, not security. The server does NOT check ownership until PR-A2 (customer send, close, subject,
              category); until then this gate is the only thing stopping it. Reopen is gone on purpose: a closed
              conversation is terminal, a returning customer gets a new one. */}
          {isConsulting && conv.consultationBotId && !actionsBlocked && (
            <button
              className="ac-btn ghost sm"
              onClick={() =>
                runStateChange(() =>
                  hub.dismissConsultation(sessionId, conv.consultationBotId as string, conv.channel || 'web'),
                )
              }
              data-testid="let-bot-continue"
            >
              <Icon name="release" size={15} />
              {t('btn_letbot')}
            </button>
          )}
          {!closed && !mineTakeover && !actionsBlocked && (
            <button className="ac-btn primary sm" onClick={doTakeover} data-testid="takeover-btn">
              <Icon name="takeover" size={15} />
              {t('btn_takeover')}
            </button>
          )}
          {mineTakeover && !closed && (
            <button
              className="ac-btn ghost sm"
              onClick={() =>
                runStateChange(async () => {
                  await hub.releaseSession(sessionId);
                  // Same as takeover/close: re-read THIS row so this console's copy stops naming me as the owner
                  // (the state push moves `state` only; a stale ownerAgentId is what useOwnerVerification exists
                  // to distrust).
                  void refreshRow(sessionId);
                })
              }
              data-testid="release-btn"
            >
              <Icon name="release" size={15} />
              {t('btn_release')}
            </button>
          )}
          {!closed && !actionsBlocked && (
            <button
              className="ac-btn ghost sm"
              onClick={openCloseDialog}
              data-testid="close-btn"
            >
              <Icon name="x" size={15} />
              {t('closeConversation')}
            </button>
          )}
          {/* Move to folder, Return to team and Spam / unwanted live in the "more" menu (operator 2026-10-12), offered under
              exactly the conditions these buttons had; the test ids are unchanged. */}
          <ConversationMenu
            conv={conv}
            canMove={!actionsBlocked}
            canLearn={!actionsBlocked}
            returnMode={returnMode}
            onReturnToTeam={() => {
              setReturnError(null);
              setReturnOpen(true);
            }}
            onSpam={canMarkSpam && !actionsBlocked ? openSpamDialog : null}
          />
        </div>
      </div>

      {canMarkSpam && spamOpen && !actionsBlocked && (
        <SpamConfirmDialog
          busy={spamBusy}
          error={spamError}
          onConfirm={confirmSpam}
          onCancel={cancelSpam}
        />
      )}

      {closeOpen && !closed && !actionsBlocked && (
        <CloseConversationDialog
          key={sessionId}
          sessionId={sessionId}
          busy={closeBusy}
          error={closeError}
          onConfirm={confirmCloseWith}
          onCancel={() => {
            if (closeBusy) return;
            setCloseOpen(false);
            setCloseError(null);
          }}
        />
      )}

      {returnOpen && returnMode && (
        <ReturnToTeamDialog
          mode={returnMode}
          ownerName={(owner && directory[owner]) || t('agentFallbackName')}
          busy={returnBusy}
          error={returnError}
          onConfirm={doReturnToTeam}
          onCancel={() => {
            if (returnBusy) return;
            setReturnOpen(false);
            setReturnError(null);
          }}
        />
      )}

      {actionError && (
        <div
          role="alert"
          data-testid="thread-action-error"
          style={{
            padding: '8px 20px',
            fontSize: 12.5,
            color: 'var(--danger)',
            background: 'var(--danger-soft)',
            fontWeight: 600,
          }}
        >
          {t('actionFailedRetry')}
        </div>
      )}

      {/* The conversation's tags: attach / detach EXISTING catalog tags (D8). Keyed on the session so a half-typed filter
          or an in-flight toggle of the previous conversation can never carry over. A read-only viewer sees the chips only. */}
      {/* Tags and the timeline filter share ONE row (operator 2026-10-12: two bars of chrome above the messages). The filter
          narrows what is SHOWN; the bar below decides where an answer GOES. */}
      <div className="ac-chat-sub">
        {(!tagsReadOnly || convTags.length > 0) && (
          <div className="tags-sub" data-testid="chat-tags">
            <TagPicker key={sessionId} sessionId={sessionId} selected={convTags} readOnly={tagsReadOnly} />
          </div>
        )}
        <TimelineFilter value={filter} counts={counts} onChange={changeFilter} consulting={isConsulting} />
      </div>

      {askedForPerson && (
        <div className="ac-human-banner" role="alert" data-testid="human-requested-banner">
          <Icon name="hand" size={18} />
          <span className="ac-human-banner-text" dir="auto">
            {t('human_banner')}
          </span>
          {canTakeOverHere && (
            <button
              type="button"
              className="ac-btn primary sm"
              onClick={doTakeover}
              data-testid="human-requested-takeover"
            >
              <Icon name="takeover" size={15} />
              {t('btn_takeover')}
            </button>
          )}
        </div>
      )}

      <div className="ac-timeline" ref={scrollRef} data-testid="chat-timeline">
        {shown.length === 0 && filter !== 'all' && (
          <div className="ac-timeline-empty" data-testid="timeline-empty">
            {t('tl_empty_filter')}
          </div>
        )}
        {shown.map((m) => (
          <MsgBubble
            key={m.messageId}
            m={m}
            customerName={customerName}
            onOpenConsultation={() => changeFilter('consult')}
            replyActions={replyActions}
          />
        ))}
        {!isTakenOver && isBotThinking && filter !== 'consult' && (
          <div className="ac-msg bot" data-testid="bot-thinking-indicator">
            <span className="msg-av bot" aria-hidden="true">
              <Icon name="bot" size={16} />
            </span>
            <div className="ac-msg-body">
              <div className="ac-bubble" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="ac-msg-sender bot">
                  <Icon name="bot" size={12} />
                </span>
                <span className="ac-typing" style={{ color: 'var(--info)' }}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ADR-019 — once a human has taken the conversation over they are writing to the customer directly, so a
          bot draft awaiting approval is moot; Takeover is ADR-019's escape hatch (there is deliberately no
          Discard button), so the card simply stops rendering rather than needing its own dismiss action. */}
      {cardDraft && !isTakenOver && (
        <DraftReviewCard draft={cardDraft} myAgentId={me} onAct={onDraftAct} />
      )}

      {/* ONE answer bar. Its mode follows the conversation's STATE and OWNER, never the filter above; a conversation
          someone else owns (or an unknown owner during takeover) gets a read-only banner and no input; a closed one
          is read-only text. `owner` is the verified wire owner and `holder` the verified holder (utils/ownership.ts
          holderId) computed above; the bar's lock and mode read the holder. */}
      <AnswerBar
        conv={conv}
        customerName={customerName}
        owner={owner}
        holder={holder}
        me={me}
        ownerUnverified={ownerConfirming}
        ownerUnconfirmed={ownerUnverified && !ownerConfirming}
        onSendCustomer={sendCustomer}
        onSendGuidance={sendGuidance}
        onTakeover={doTakeover}
        onTyping={() => hub.signalAgentTyping(sessionId)}
        // A refused send means the owner changed under us; re-read THIS row so the bar learns the truth from the
        // server instead of guessing (a 404 answer hides the row: it is no longer ours to see).
        onRefused={() => void refreshRow(sessionId)}
        prefill={barPrefill}
        onPrefillApplied={(seq) => setPrefill((p) => (p && p.seq === seq ? null : p))}
      />
    </div>
  );
};
